import type { ReactNode } from "react";

import clsx from "clsx";
import { Button } from "react-aria-components";

import { ArrowDownIcon } from "../../icons/arrow-down";
import { Collapsible } from "../collapsible";

import styles from "./expandable-card.module.css";

type ExpandableCardProps = {
	children: ReactNode;
	description?: ReactNode;
	isExpanded: boolean;
	onExpandedChange: (isExpanded: boolean) => void;
	/**
	 * Recap of what the card holds, shown under the header so it stays visible while
	 * collapsed. Sits outside the trigger, so it does not toggle the card.
	 */
	summary?: ReactNode;
	title: string;
};

/**
 * A bordered block whose header toggles the panel below it. Keep several in a row and
 * drive `isExpanded` from one piece of state to get accordion behaviour.
 */
export function ExpandableCard({
	children,
	description,
	isExpanded,
	onExpandedChange,
	summary,
	title,
}: ExpandableCardProps): ReactNode {
	return (
		<div className={clsx(styles.card, isExpanded && styles.expanded)}>
			<Collapsible
				header={() => {
					return (
						<>
							<Button className={styles.header} slot="trigger">
								<span className={styles.text}>
									<span className={clsx("body-1", "bold", styles.title)}>
										{title}
									</span>
									{description && (
										<span className={clsx("caption", styles.description)}>
											{description}
										</span>
									)}
								</span>
								<ArrowDownIcon
									className={clsx(
										styles.chevron,
										isExpanded && styles.chevronExpanded,
									)}
								/>
							</Button>
							{summary && <div className={styles.summary}>{summary}</div>}
						</>
					);
				}}
				isExpanded={isExpanded}
				onExpandedChange={onExpandedChange}
			>
				<div className={styles.panel}>{children}</div>
			</Collapsible>
		</div>
	);
}
